import { Component, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { Observable } from 'rxjs/Observable';
import { OnyxEvent } from '../../event/models/event';
import { FlashMessagesService } from '../../shared/flash-messages/service/flash.messages.service';
import { PioneerValidators } from '../../shared/utils/pioneer.validators';
import { PaymentEquation } from '../model/payment-equation';
import { PaymentRequest } from '../model/payment-request';
import { PaymentActionEnum, PaymentActions } from '../payment-action.enum';
import { PaymentRejectionReasonsComponent } from '../payment-rejection-reasons/payment-rejection-reasons.component';
import { PaymentRequestStatusEnum } from '../payment-request-status.enum';
import { CommissionService, CommissionStore } from '../service';

@Component({
  selector: 'pioneer-payment-action',
  templateUrl: './payment-action.component.html',
  styleUrls: ['./payment-action.component.css']
})
export class PaymentActionComponent implements OnInit {

  @ViewChild(PaymentRejectionReasonsComponent) rejectionReason: PaymentRejectionReasonsComponent;

  event: OnyxEvent;
  paymentEquation: PaymentEquation;

  action: string;
  actioned: string;
  oppositeAction: string;

  // page info
  title: string;
  summary: string;
  emailLabel: string;

  isCompleted = false;

  form: FormGroup = this.fb.group({
    email: ['', [Validators.required, Validators.maxLength(75), PioneerValidators.isValidEmail]]
  });

  get email() { return this.form.get('email'); }

  constructor(
    private route: ActivatedRoute,
    private translate: TranslateService,
    private commissionStore: CommissionStore,
    private commissionService: CommissionService,
    private fb: FormBuilder,
    private flashMessagesService: FlashMessagesService
  ) { }

  ngOnInit() {
    this.route.queryParams
      .filter(params => params.action)
      .flatMap(params => {
        if (this.isCompleted) {
          return Observable.empty();
        }
        this.updateSummary(params.action);
        this.form = this.fb.group({
          email: [this.email.value, [Validators.required, Validators.maxLength(75), PioneerValidators.isValidEmail]]
        });
        return this.commissionStore.paymentRequest;
      })
      .subscribe((paymentRequest: PaymentRequest) => {
        this.event = paymentRequest.event;
        if (paymentRequest.event && paymentRequest.event.eventProperty) {
          this.event.groupCodeNumber = paymentRequest.event.eventProperty.eventReferenceNumber;
        }

        this.paymentEquation = new PaymentEquation({
          roomCommissions: paymentRequest.roomCommissions,
          adjustments: paymentRequest.adjustments,
          totalPaymentRequested: paymentRequest.finalPayment ? paymentRequest.finalPayment.commissionAmount : 0.00,
        });
      });
  }

  onSubmit() {
    let summary;

    if (this.action === PaymentActionEnum.CONFIRM.toString()) {
      this.commissionService.approvePaymentRequest(this.email.value)
        .subscribe(() => {
          this.isCompleted = true;
          summary = this.translate.instant('EVENT.COMMISSION.CONFIRMED_SUMMARY');
          this.setPageInfo(
            this.translate.instant('EVENT.COMMISSION.ACTIONED', { actioned: this.actioned }),
            summary);
          this.flashMessagesService.success('EVENT.COMMISSION.ACTIONED', null, { actioned: this.actioned });
        }, (err) => {
          this.isCompleted = false;
          this.flashMessagesService.error('MESSAGES.ERROR.APPROVE_PAYMENT_REQUEST', `Error approving payment request: ${ err }`);
          return Observable.of(err);
        });

    } else {
      this.commissionService.rejectPaymentRequest(
        this.email.value,
        this.rejectionReason.description.value,
        this.rejectionReason.details.value)
        .subscribe(() => {
          this.isCompleted = true;
          summary = this.translate.instant('EVENT.COMMISSION.REJECTED_SUMMARY');
          this.setPageInfo(
            this.translate.instant('EVENT.COMMISSION.ACTIONED', { actioned: this.actioned }),
            summary);
          this.flashMessagesService.success('EVENT.COMMISSION.ACTIONED', null, { actioned: this.actioned });
        }, (err) => {
          this.isCompleted = false;
          this.flashMessagesService.error('MESSAGES.ERROR.REJECT_PAYMENT_REQUEST', `Error rejecting payment request: ${ err }`);
          return Observable.of(err);
        }
        );
    }
  }

  updateSummary(action: string) {
    if (PaymentActions.indexOf(action) >= 0) {
      this.action = action;

      this.translate.get('EVENT.COMMISSION.CONFIRM').subscribe(() => {
        if (action === PaymentActionEnum.CONFIRM.toString()) {
          this.setConfirmDetails();
        } else {
          this.setRejectDetails();
        }
      });
    }
  }

  setConfirmDetails() {
    this.actioned = PaymentRequestStatusEnum.CONFIRMED.toString();
    this.oppositeAction = PaymentActionEnum.REJECT.toString();
    this.setPageInfo(
      this.translate.instant('EVENT.COMMISSION.ACTION', { action: PaymentActionEnum.CONFIRM.toString() }),
      this.translate.instant('EVENT.COMMISSION.CONFIRM_SUMMARY'),
      this.translate.instant('EVENT.COMMISSION.ACTION_BY', { actionBy: PaymentRequestStatusEnum.APPROVED.toString() })
    );
  }

  setRejectDetails() {
    this.actioned = PaymentRequestStatusEnum.REJECTED.toString();
    this.oppositeAction = PaymentActionEnum.CONFIRM.toString();
    this.setPageInfo(
      this.translate.instant('EVENT.COMMISSION.ACTION', { action: PaymentActionEnum.REJECT.toString() }),
      this.translate.instant('EVENT.COMMISSION.REJECT_SUMMARY'),
      this.translate.instant('EVENT.COMMISSION.ACTION_BY', { actionBy: PaymentRequestStatusEnum.REJECTED.toString() })
    );
  }

  setPageInfo(title: string, summary: string, emailLabel?: string) {
    this.title = title;
    this.summary = summary;
    this.emailLabel = emailLabel;
  }

}



// WEBPACK FOOTER //
// /var/lib/jenkins/jobs/GroupPay/jobs/QA2/jobs/QA-Pioneer-ALL-2-POC/workspace/pioneer-web/src/app/payment/payment-action/payment-action.component.ts